import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Box, Dialog, DialogActions, DialogContent, IconButton, Slider, Typography } from "@mui/material";
import { message } from "antd";
import SpinFC from "antd/es/spin";
import React, { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { useDropzone } from "react-dropzone";

import ActionButton from "../buttons/ActionButton";
import { axiosUploadInstance } from "../../../axios/axios-instance";
import { pathAPI } from "../../../utils/pathApi";
import { getCroppedImg } from "../../../utils/utils";

interface UploadImageServeProps {
  width?: number | string;
  /** Kept for compatibility; the field is always square */
  height?: number;
  urlImage?: string | null;
  onChangeFile?: (file: string | null) => void;
  loadingProps?: boolean;
  disabled?: boolean;
  /** Awaiting approval: shown in place of the current image, locked */
  pendingUrl?: string | null;
}

const UploadImageServe: React.FC<UploadImageServeProps> = ({
  width = "100%",
  urlImage = null,
  onChangeFile,
  loadingProps = false,
  disabled = false,
  pendingUrl = null,
}) => {
  const [previewImage, setPreviewImage] = useState<string>(urlImage || "");
  const [file, setFile] = useState<string | null>(urlImage || null);
  const [loading, setLoading] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);

  const isLoading = loading || loadingProps;

  useEffect(() => {
    onChangeFile?.(file);
  }, [file]);

  useEffect(() => {
    if (urlImage) {
      setPreviewImage(urlImage);
    }
  }, [urlImage]);

  const handleFileSelect = async (file: File) => {
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
      message.error("Chỉ hỗ trợ JPG, PNG, WEBP");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      message.error("Ảnh quá lớn. Tối đa 5MB.");
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      setImageSrc(reader.result as string);
      setCropModalOpen(true);
    };
  };

  const handleCropComplete = useCallback((_: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const uploadCroppedImage = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    try {
      setLoading(true);
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels, rotation, "blob");
      const formData = new FormData();
      formData.append("file", croppedBlob as Blob, "cropped-image.png");

      const res = await axiosUploadInstance.post(pathAPI.uploadImage.replace(":type", "IMAGE"), formData);
      const url = res.data.data.url;
      setFile(url);
      setPreviewImage(url);
    } catch (err) {
      console.error("Upload error", err);
      message.error("Lỗi khi upload ảnh");
    } finally {
      setLoading(false);
      setCropModalOpen(false);
    }
  };

  const handleRemove = () => {
    setFile(null);
    setPreviewImage("");
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) handleFileSelect(file);
    },
    disabled,
    multiple: false,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp'],
    },
  });

  return (
    <Box width={width} sx={{ aspectRatio: "1 / 1" }} position="relative" overflow="hidden" borderRadius="8px">
      {isLoading ? (
        <Box
          width="100%"
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          border="1px dashed var(--app-border)"
          borderRadius="8px"
        >
          <SpinFC tip="Loading" />
        </Box>
      ) : previewImage || urlImage || pendingUrl ? (
        <Box position="relative" width="100%" height="100%">
          <img
            src={pendingUrl || previewImage}
            alt="Preview"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: 8,
            }}
          />
          {pendingUrl && (
            <Box
              position="absolute"
              left={6}
              bottom={6}
              px={1}
              borderRadius="10px"
              fontSize={11}
              color="#fff"
              bgcolor="#ed6c02"
            >
              Pending
            </Box>
          )}
          {!disabled && !pendingUrl && (
            <Box
              position="absolute"
              top={0}
              left={0}
              bgcolor="rgba(0,0,0,0.5)"
              width="100%"
              height="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{ cursor: "pointer",
                borderRadius: "10px",
                "& .delete-icon": {
                  opacity: 0.3,
                  transition: "opacity 0.3s",
                },
                "&:hover .delete-icon": {
                  opacity: 1,
                }, }}
            >
              <IconButton onClick={handleRemove} sx={{ color: "#fff" }} className="delete-icon">
                <DeleteOutlined  />
              </IconButton>
            </Box>
          )}
        </Box>
      ) : (
        <Box
          {...getRootProps()}
          border="1px dashed var(--app-border)"
          borderRadius={2}
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          width="100%"
          height="100%"
          textAlign="center"
          sx={{ cursor: disabled ? "not-allowed" : "pointer", bgcolor: "action.hover", padding: 2 }}
        >
          <input {...getInputProps()} />
          <PlusOutlined style={{ fontSize: 24 }} />
          <Typography mt={1}>
            {isDragActive ? "Thả ảnh vào đây..." : "Tải ảnh lên hoặc kéo thả ảnh vào đây"}
          </Typography>
        </Box>
      )}

      {/* Crop Dialog */}
      <Dialog open={cropModalOpen} onClose={() => setCropModalOpen(false)} fullWidth maxWidth="sm">
        <DialogContent sx={{ height: 400, position: "relative" }}>
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={1}
              showGrid={false}
              cropShape="round"
              onCropChange={setCrop}
              onRotationChange={setRotation}
              onZoomChange={setZoom}
              onCropComplete={handleCropComplete}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ flexDirection: "column", alignItems: "stretch", px: 3 }}>
          <Box width="100%">
            <label>Zoom</label>
            <Slider value={zoom} min={1} max={3} step={0.1} onChange={(_, v) => setZoom(v as number)} />
          </Box>
          <Box width="100%">
            <label>Xoay ảnh</label>
            <Slider value={rotation} min={0} max={360} step={1} onChange={(_, v) => setRotation(v as number)} />
          </Box>
          <ActionButton onClick={uploadCroppedImage}>Upload</ActionButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UploadImageServe;
